import { useQuery } from '@tanstack/react-query';
import { getCampaignById } from '@/services/smsApi';
import { useAuth } from '@/context/auth';
import { toast } from '@/hooks/use-toast';

/**
 * Hook for fetching a single campaign's details by ID
 */
export function useCampaignDetails(campaignId: string | undefined) {
  const { user } = useAuth();
  
  // Get restaurant ID from user context
  const restaurantId = user?.restaurants?.[0]?.id;
  
  return useQuery({
    queryKey: ['campaignDetails', campaignId, restaurantId],
    queryFn: async () => {
      if (!campaignId) {
        throw new Error('Campaign ID is required');
      }
      
      console.log(`Fetching campaign details for ID: ${campaignId}`);
      
      const response = await getCampaignById(campaignId, restaurantId);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch campaign details');
      }
      
      return response.campaign;
    },
    enabled: !!campaignId && !!user, // Only run query when we have both campaign ID and user
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000),
    onError: (error) => {
      console.error('Error fetching campaign details:', error);
      toast({
        title: "Failed to load campaign",
        description: error instanceof Error ? error.message : "An unknown error occurred",
        variant: "destructive"
      });
    }
  });
}